// Composer Studio — 템플릿 전역 속성 「분할 축」 저작 로직(순수 함수)
//
// 저작이 선언하는 것은 **축 하나**다: "이 양식은 나눌 수 있나, 무엇으로". 나눌지 말지는
// 작성자가 진입 시 고른다(결정 2026-09-01 §③-1 — `SplitMode`는 정책이 담는 축이 아니다).
// 그래서 이 화면의 칸은 select 하나이고, 그 값은 `policy.split.provider`다.
//
// UI(SplitAxisSection.tsx)와 분리한 이유: 축 전환은 **기존 세부 선언을 보존하느냐 지우느냐**가
// 전부인데, 그 판정을 12k줄 EditorPage 안에서 RTL로 검사하려면 비용이 성질에 비해 과하다.
import type { SplitPolicy, TemplatePolicy } from '@safetyhub/shared';

/**
 * 「분할 축」 선택지 — **손으로 적는 목록**이다. 서버 registry와 자동 동기화하지 않는다.
 *
 * 축을 늘리려면 서버에 provider를 구현·등록해야 하고(선언만으로 새 축을 만드는 능력은 2026-09-01에
 * 제거됐다 — `SplitPolicy` JSDoc), 그때 이 배열에 한 줄을 더한다. registry를 admin API로 노출해
 * 옵션을 끌어오는 기전은 축이 하나뿐인 지금 값보다 표면이 크다.
 *
 * ⚠️ 라벨은 **「작업 분류」**다 — 「공종」이 아니다(결정 2026-09-02 ③절). 축의 정체가 공종이
 * 아니라 작업 분류이고, 옛 어휘를 저작 화면에 박으면 저작자가 그 어휘로 양식을 만든다.
 * 「(TBM)」 접미도 붙이지 않는다(2026-09-03 사용자 확정) — 이 축은 TBM 전용이 아니라 어느
 * 양식이든 지목할 수 있는 분류 classifier다. TBM은 등록 키(`tbm-work-type`)에만 남는 구현
 * 내력이고, 키는 DB 템플릿 행(`policy.split.provider`)이 참조하므로 리네임하지 않는다.
 */
export const SPLIT_AXIS_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: '', label: '없음' },
  { value: 'tbm-work-type', label: '작업 분류' },
];

/** select의 현재 값 — 미지정/빈 provider는 「없음」(registry의 `resolvePolicy`와 같은 판정). */
export function getSplitAxisProvider(policy: TemplatePolicy | undefined): string {
  const provider = policy?.split?.provider;
  return typeof provider === 'string' ? provider : '';
}

/**
 * 축 전환 결과 정책.
 *  - `''`(없음) → `split` 축 **제거**. 세부 선언도 함께 사라지므로 호출부가 먼저 확인을 받는다.
 *  - provider 키 → 기존 세부(`source`/`idPath`/`rowLabel`/`childSources`/`groupLabelTo` 등)를
 *    **보존**하고 provider만 교체한다. 세부는 이 화면에 편집 칸이 없어(코드·seed가 소유),
 *    여기서 지우면 되돌릴 방법이 없다.
 *
 * `policy` 자체가 없던 템플릿에 축을 지정하면 정책 객체가 생긴다 — 저장 정규화
 * (`normalizeTemplatePolicy`)가 빈 정책을 null로 접으므로 「없음」 왕복은 원래 상태로 돌아간다.
 */
export function applySplitAxis(
  policy: TemplatePolicy | undefined,
  provider: string,
): TemplatePolicy {
  const next: TemplatePolicy = { ...policy };
  if (!provider) {
    delete next.split;
    return next;
  }
  next.split = { ...next.split, provider };
  return next;
}

/**
 * 「없음」 전환 시 함께 사라질 세부 선언 — provider 외에 실제로 값이 있는 축만 사람 말로 센다.
 *
 * 목록을 손으로 적지 않고 `SplitPolicy`의 키에서 파생시킨다. 손나열은 "지금은 맞다"만 보장하고,
 * 새 축이 `SplitPolicy`에 생기면 그 축만 조용히 경고에서 빠져 *"안내에 없던 것이 사라지는"*
 * 상태가 된다. `Record<Exclude<keyof SplitPolicy,'provider'>, string>`이라 축이 추가되면
 * **이 파일이 컴파일되지 않는다**(tsc가 누락 속성을 잡는다).
 */
const SPLIT_DETAIL_LABELS: Record<Exclude<keyof SplitPolicy, 'provider'>, string> = {
  source: '분할 대상 데이터',
  idPath: '행 식별자 경로',
  rowLabel: '행 표시명',
  rowSublabel: '행 보조 표시',
  rowPhoto: '행 표시 사진',
  childSources: '자식 배열 전파',
  groupLabelTo: '그룹명 주입 칸',
};

/** 값이 있는 세부 축의 사람 말 목록(선언 순) — 비어 있으면 잃을 것이 없다. */
export function describeSplitDetail(policy: TemplatePolicy | undefined): string[] {
  const split = policy?.split;
  if (!split) return [];
  return (Object.keys(SPLIT_DETAIL_LABELS) as Array<keyof typeof SPLIT_DETAIL_LABELS>)
    .filter((key) => {
      const value = split[key];
      return Array.isArray(value) ? value.length > 0 : value !== undefined && value !== '';
    })
    .map((key) => SPLIT_DETAIL_LABELS[key]);
}

/** 「없음」 전환 전에 확인을 받아야 하나 — provider만 있으면 잃을 세부가 없어 바로 지운다. */
export function hasSplitDetail(policy: TemplatePolicy | undefined): boolean {
  return describeSplitDetail(policy).length > 0;
}
